// src/components/common/Navbar.jsx
import { AppBar, Toolbar, Button, Box, Menu, MenuItem, Divider } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

export default function Navbar() {
  const { user, authReady, logout } = useAuth();
  const navigate = useNavigate();
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [avatarAnchor, setAvatarAnchor] = useState(null);

  const raw = JSON.parse(localStorage.getItem('user')) || {};
  const username = raw.username || 'Anonymous';
  const avatarLetter = username[0] ? username[0].toUpperCase() : 'U';

  useEffect(() => {
    if (user) {
      fetchProfilePictureUrl();
    } else {
      setProfilePictureUrl(null);
    }
  }, [user]);

  async function fetchProfilePictureUrl() {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('rv_access_token');
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE}/api/profile/picture-url`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProfilePictureUrl(data.url);
      }
    } catch (error) {
      console.error('Failed to fetch profile picture:', error);
    }
  }

  return (
    <AppBar
      position="sticky"
      sx={{
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        bgcolor: 'primary.main',
        boxShadow: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Toolbar sx={{ px: 3 }}>
        {/* Left side: navigation links */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Logo — same asset and markup as the masthead in Home.jsx (see the note there
              on the PNG). Sized to this 64px bar rather than the taller marketing one, and
              hidden below sm, where the links and avatar already overflow a 360px bar.
              Points where Home does: "/" is the marketing page even when signed in. */}
          <Box
            component={Link}
            to={user ? '/home' : '/'}
            aria-label="ReVisionari home"
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center', flexShrink: 0, lineHeight: 0 }}
          >
            <Box
              component="img"
              src="/images/revisionari-logo.png"
              alt="ReVisionari"
              sx={{ height: 36, width: 'auto', display: 'block' }}
            />
          </Box>

          {/* Home — signed-in users get their own landing page; "/" stays the
              marketing page for visitors. This Navbar renders on nearly every
              route, so the only thing that may change here is the target. */}
          <Button
            component={Link}
            to={user ? '/home' : '/'}
            sx={{
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            Home
          </Button>

          {/* Dashboard (inserted here) */}
          {user && (
            <Button
              component={Link}
              to="/dashboard"
              sx={{
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              Dashboard
            </Button>
          )}

          {/* Library */}
          <Button
            component={Link}
            to="/library"
            sx={{
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            Library
          </Button>

          {/* Role-restricted options */}
          {user && (
            <>
              <Button
                component={Link}
                to="/editor-home"
                sx={{
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                Create
              </Button>
            </>
          )}
        </Box>

        {/* Right side: session indicator — avatar = active, Sign In = expired/logged out */}
        {!authReady ? null : !user ? (
          <Button
            component={Link}
            to="/login"
            variant="outlined"
            sx={{
              color: 'primary.contrastText',
              borderColor: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
                borderColor: 'primary.contrastText',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease',
              fontWeight: 'bold',
              fontSize: '0.75rem',
            }}
          >
            Sign In
          </Button>
        ) : (
          <>
            <Box
              onClick={(e) => setAvatarAnchor(e.currentTarget)}
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                overflow: 'hidden',
                bgcolor: 'primary.dark',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.contrastText',
                fontWeight: 'bold',
                fontSize: '1rem',
                cursor: 'pointer',
                border: '2px solid',
                borderColor: 'primary.contrastText',
                '&:hover': { opacity: 0.85 },
                transition: 'opacity 0.2s ease',
              }}
            >
              {profilePictureUrl ? (
                <img
                  src={profilePictureUrl}
                  alt="Profile"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                avatarLetter
              )}
            </Box>
            <Menu
              anchorEl={avatarAnchor}
              open={Boolean(avatarAnchor)}
              onClose={() => setAvatarAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { setAvatarAnchor(null); navigate('/settings'); }}>
                Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { setAvatarAnchor(null); logout(); }} sx={{ color: 'error.main' }}>
                Logout
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}
